import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Divider, Grid } from '@material-ui/core'
import { Datagrid, Loader } from '@refera/ui-web'
import { navigate, useParams } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import HeaderTitle from '_/components/header-title'
import { ROUTES } from '_/utils/constants'
import { parseNumber, phoneNumberMask } from '_/utils/helpers'
import Button from '_/components/button'
import classNames from 'classnames'
import { BasicInput, MaskBasicInput } from '_/components/inputs'
import * as Modal from '_components/modal/generic-modal'
import { FormProvider, useForm } from 'react-hook-form'
import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { agencySelector } from '_/modules/agency/selectors'
import { MESSAGE_CHANNEL_STATUS, MESSAGE_CHANNEL_STATUS_LABELS } from '../constants'
import {
  connectMessageChannel,
  createMessageChannel,
  disconnectMessageChannel,
  getAgencyFull,
  getMessageChannelQrCode,
  getMessageChannelStatus,
  getMessageChannels,
  testMessageChannelConnection,
} from '_/modules/agency/actions'
import useStyles, { datagridStyle } from './styles'

const createStatusConfig = styles => ({
  [MESSAGE_CHANNEL_STATUS.CONNECTED]: {
    label: MESSAGE_CHANNEL_STATUS_LABELS[MESSAGE_CHANNEL_STATUS.CONNECTED],
    className: styles.statusPillActive,
  },
  [MESSAGE_CHANNEL_STATUS.DISCONNECTED]: {
    label: MESSAGE_CHANNEL_STATUS_LABELS[MESSAGE_CHANNEL_STATUS.DISCONNECTED],
    className: styles.statusPillDisconnected,
  },
  [MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER]: {
    label: MESSAGE_CHANNEL_STATUS_LABELS[MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER],
    className: styles.statusPillDisconnected,
  },
  [MESSAGE_CHANNEL_STATUS.WAITING_QR]: {
    label: MESSAGE_CHANNEL_STATUS_LABELS[MESSAGE_CHANNEL_STATUS.WAITING_QR],
    className: styles.statusPillWaiting,
  },
  [MESSAGE_CHANNEL_STATUS.WAITING_CONNECTION]: {
    label: MESSAGE_CHANNEL_STATUS_LABELS[MESSAGE_CHANNEL_STATUS.WAITING_CONNECTION],
    className: styles.statusPillWaiting,
  },
})

const useQrCodeFlow = ({
  getTargetConnectionId,
  getConnectionName,
  getMessageChannelQrCodeAction,
  getMessageChannelStatusAction,
  fetchConnections,
  onConnectionReady,
  showToast,
}) => {
  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false)
  const [qrCodeValue, setQrCodeValue] = useState('')
  const [qrCodeConnectionName, setQrCodeConnectionName] = useState('')
  const [isQrLoading, setIsQrLoading] = useState(false)
  const [isQrCodeError, setIsQrCodeError] = useState(false)
  const [qrCodeErrorMessage, setQrCodeErrorMessage] = useState('')
  const [qrCloseSeconds, setQrCloseSeconds] = useState(40)
  const [isQrCloseDisabled, setIsQrCloseDisabled] = useState(true)
  const [qrCodeButtonLabel, setQrCodeButtonLabel] = useState('Ler QR Code')
  const qrCheckIntervalRef = useRef(null)
  const qrTimeoutRef = useRef(null)
  const qrCloseIntervalRef = useRef(null)

  const clearQrTimers = useCallback(() => {
    if (qrCheckIntervalRef.current) {
      clearInterval(qrCheckIntervalRef.current)
      qrCheckIntervalRef.current = null
    }
    if (qrTimeoutRef.current) {
      clearTimeout(qrTimeoutRef.current)
      qrTimeoutRef.current = null
    }
    if (qrCloseIntervalRef.current) {
      clearInterval(qrCloseIntervalRef.current)
      qrCloseIntervalRef.current = null
    }
  }, [])

  const resolveQrCodeError = useCallback(error => {
    const errorValue = error?.response?.data?.error
    if (typeof errorValue !== 'string') {
      return null
    }
    if (/qrcode_expired|expired/i.test(errorValue)) {
      return 'QR Code expirado. Gere um novo QR Code.'
    }
    return null
  }, [])

  const openQrCodeModal = useCallback(async () => {
    const targetConnectionId = getTargetConnectionId()
    if (!targetConnectionId) {
      showToast({ type: 'error' })
      return
    }
    try {
      setIsQrLoading(true)
      const response = await getMessageChannelQrCodeAction(targetConnectionId)
      if (!response?.qrcode) {
        showToast({ type: 'error' })
        return
      }
      setQrCodeValue(response.qrcode)
      setQrCodeConnectionName(getConnectionName())
      setIsQrCodeModalOpen(true)
      setIsQrCodeError(false)
      setQrCodeErrorMessage('')
      setQrCodeButtonLabel('Ler QR Code')
    } catch (error) {
      const errorMessage = resolveQrCodeError(error)
      if (errorMessage) {
        showToast({ type: 'error', message: errorMessage })
        setIsQrCodeError(true)
        setQrCodeErrorMessage(errorMessage)
      } else {
        showToast({ type: 'error' })
      }
    } finally {
      setIsQrLoading(false)
    }
  }, [
    getConnectionName,
    getMessageChannelQrCodeAction,
    getTargetConnectionId,
    resolveQrCodeError,
    showToast,
  ])

  const handleCloseQrCodeModal = useCallback(async () => {
    const shouldUpdateLabel = isQrCodeError || Boolean(qrCodeErrorMessage)
    if (shouldUpdateLabel) {
      setQrCodeButtonLabel('Gerar QR Code')
    }
    clearQrTimers()
    setIsQrCodeModalOpen(false)
    setQrCodeValue('')
    setQrCodeConnectionName('')
    setIsQrCodeError(false)
    setQrCodeErrorMessage('')
    const targetConnectionId = getTargetConnectionId()
    if (!targetConnectionId) {
      return
    }
    try {
      const response = await getMessageChannelStatusAction(targetConnectionId)
      if (response?.connected) {
        onConnectionReady()
        await fetchConnections()
      }
    } catch (error) {
      showToast({ type: 'error' })
    }
  }, [
    clearQrTimers,
    fetchConnections,
    getMessageChannelStatusAction,
    getTargetConnectionId,
    isQrCodeError,
    onConnectionReady,
    qrCodeErrorMessage,
    showToast,
  ])

  useEffect(() => {
    clearQrTimers()
    if (!isQrCodeModalOpen) {
      return undefined
    }
    setIsQrCodeError(false)
    setQrCodeErrorMessage('')
    setQrCloseSeconds(40)
    setIsQrCloseDisabled(true)
    const targetConnectionId = getTargetConnectionId()
    if (!targetConnectionId) {
      return undefined
    }
    qrCloseIntervalRef.current = setInterval(() => {
      setQrCloseSeconds(current => {
        if (current <= 1) {
          setIsQrCloseDisabled(false)
          if (qrCloseIntervalRef.current) {
            clearInterval(qrCloseIntervalRef.current)
            qrCloseIntervalRef.current = null
          }
          return 0
        }
        return current - 1
      })
    }, 1000)
    const handleConnectionSuccess = async () => {
      clearQrTimers()
      setIsQrCodeModalOpen(false)
      setQrCodeValue('')
      setQrCodeConnectionName('')
      onConnectionReady()
      showToast({ type: 'success', message: 'Conexão realizada com sucesso' })
      await fetchConnections()
    }
    const checkQrStatus = async () => {
      try {
        const response = await getMessageChannelStatusAction(targetConnectionId)
        if (response?.connected) {
          await handleConnectionSuccess()
        }
      } catch (error) {
        const errorMessage = resolveQrCodeError(error)
        if (errorMessage) {
          showToast({ type: 'error', message: errorMessage })
          setQrCodeErrorMessage(errorMessage)
          clearQrTimers()
        } else {
          showToast({ type: 'error' })
          setQrCodeErrorMessage('')
        }
        setIsQrCodeError(true)
      }
    }
    qrCheckIntervalRef.current = setInterval(checkQrStatus, 10000)
    qrTimeoutRef.current = setTimeout(() => {
      if (qrCheckIntervalRef.current) {
        clearInterval(qrCheckIntervalRef.current)
        qrCheckIntervalRef.current = null
      }
      setIsQrCodeError(true)
      setQrCodeErrorMessage('QR Code expirado. Feche a janela e gere um novo QR Code.')
    }, 40000)
    return () => {
      clearQrTimers()
    }
  }, [
    clearQrTimers,
    fetchConnections,
    getMessageChannelStatusAction,
    getTargetConnectionId,
    isQrCodeModalOpen,
    onConnectionReady,
    resolveQrCodeError,
    showToast,
  ])

  const resetQrCodeButtonLabel = useCallback(() => {
    setQrCodeButtonLabel('Ler QR Code')
  }, [])

  return {
    isQrCodeModalOpen,
    qrCodeValue,
    qrCodeConnectionName,
    isQrLoading,
    isQrCodeError,
    qrCodeErrorMessage,
    qrCloseSeconds,
    isQrCloseDisabled,
    qrCodeButtonLabel,
    openQrCodeModal,
    handleCloseQrCodeModal,
    resetQrCodeButtonLabel,
  }
}

const ConnectionConfig = () => {
  const styles = useStyles()
  const { agencyId } = useParams()
  const dispatch = useDispatch()
  const agency = useSelector(agencySelector)
  const agencyJS = agency?.toJS()
  const { showToast } = useToast()
  const { checkIfUserDoesNotPermission } = useRolePermission()
  const [selectedConnectionId, setSelectedConnectionId] = useState(null)
  const [selectedConnectionData, setSelectedConnectionData] = useState(null)
  const [isNewConnectionModalOpen, setIsNewConnectionModalOpen] = useState(false)
  const [isNewConnectionSaved, setIsNewConnectionSaved] = useState(false)
  const [isNewConnectionConnected, setIsNewConnectionConnected] = useState(false)
  const [newConnectionId, setNewConnectionId] = useState(null)
  const [isConnectionReady, setIsConnectionReady] = useState(false)
  const [isConnectingLoading, setIsConnectingLoading] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isTestLoading, setIsTestLoading] = useState(false)
  const [isTestError, setIsTestError] = useState(false)
  const [hasPermissionToViewPage, setHasPermissionToViewPage] = useState(false)
  const [isAgencyLoading, setIsAgencyLoading] = useState(true)
  const statusConfig = useMemo(() => createStatusConfig(styles), [styles])
  const methods = useForm({
    defaultValues: {
      name: '',
      phone: '',
    },
    mode: 'onChange',
  })
  const {
    reset,
    handleSubmit,
    getValues,
    trigger,
    formState: { isValid },
  } = methods
  const handleBackNavigation = useCallback(() => {
    navigate(`${ROUTES.AGENCY}/${agencyId}`)
  }, [agencyId])

  useEffect(() => {
    let isMounted = true

    const loadAgency = async () => {
      if (!agencyId) {
        if (isMounted) setIsAgencyLoading(false)
        return
      }

      if (!agency || String(agency?.id) !== String(agencyId)) {
        setIsAgencyLoading(true)
        try {
          await dispatch(getAgencyFull(agencyId))
        } finally {
          if (isMounted) setIsAgencyLoading(false)
        }
        return
      }

      setIsAgencyLoading(false)
    }

    loadAgency()

    return () => {
      isMounted = false
    }
  }, [agency?.id, agencyId, dispatch])

  useEffect(() => {
    if (isAgencyLoading) return

    const canAccessConnectionConfig =
      agencyJS?.contractType === 'SAAS' && agencyJS?.autoMessagingSaas === true

    if (!canAccessConnectionConfig) {
      navigate(ROUTES.ACCESS_DENIED)
      return
    }

    const shouldRedirectUser = checkIfUserDoesNotPermission(
      PERMISSIONS.MESSENGER,
      PERMISSIONS_ACTIONS.READ
    )

    if (shouldRedirectUser) {
      navigate(ROUTES.ACCESS_DENIED)
    } else {
      setHasPermissionToViewPage(true)
    }
  }, [
    agencyJS?.autoMessagingSaas,
    agencyJS?.contractType,
    checkIfUserDoesNotPermission,
    isAgencyLoading,
  ])

  const [connectionRows, setConnectionRows] = useState([])
  const connectionColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Nome da conexão',
        width: 320,
        editable: false,
        cellClassName: styles.listCellLink,
      },
      {
        field: 'date',
        headerName: 'Data de criação',
        width: 200,
        editable: false,
      },
      {
        field: 'connectedPhone',
        headerName: 'Telefone conectado',
        width: 220,
        editable: false,
      },
      {
        field: 'phone',
        headerName: 'Telefone de teste',
        width: 220,
        editable: false,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        editable: false,
        renderCell: params => (
          <span className={classNames(styles.statusPill, params.row.statusClass)}>
            {params.value}
          </span>
        ),
      },
    ],
    [styles]
  )
  const formatDate = useCallback(value => {
    if (!value) return ''
    return new Date(value).toLocaleString('pt-BR')
  }, [])
  const resolveStatus = useCallback(
    statusId =>
      statusConfig[statusId] || {
        label: MESSAGE_CHANNEL_STATUS_LABELS[statusId] || statusId || '-',
        className: styles.statusPillWaiting,
      },
    [statusConfig, styles.statusPillWaiting]
  )

  const fetchConnections = useCallback(async () => {
    try {
      const response = await dispatch(getMessageChannels(agencyId))
      const items = Array.isArray(response) ? response : response?.results || []
      const rows = items.map(item => {
        const connectedPhone = item?.connectedPhoneNumber || ''
        const testPhone = item?.testPhoneNumber || ''
        const statusId = item?.status
        const statusInfo = resolveStatus(statusId)
        return {
          id: item?.id,
          name: item?.name,
          date: formatDate(item?.createdAt),
          connectedPhone: phoneNumberMask(connectedPhone),
          phone: phoneNumberMask(testPhone),
          statusId,
          status: statusInfo.label,
          statusClass: statusInfo.className,
        }
      })
      setConnectionRows(rows)
    } catch (error) {
      showToast({ type: 'error' })
    }
  }, [agencyId, dispatch, formatDate, resolveStatus, showToast])

  const getTargetConnectionId = useCallback(
    () => selectedConnectionData?.id || newConnectionId || selectedConnectionId || null,
    [newConnectionId, selectedConnectionData?.id, selectedConnectionId]
  )

  const getConnectionName = useCallback(
    () => selectedConnectionData?.name || '',
    [selectedConnectionData?.name]
  )

  const handleConnectionReady = useCallback(() => {
    setIsConnectionReady(true)
    setIsNewConnectionConnected(false)
    setSelectedConnectionData(current =>
      current ? { ...current, statusId: MESSAGE_CHANNEL_STATUS.CONNECTED } : current
    )
  }, [])

  const getMessageChannelQrCodeAction = useCallback(
    async connectionId => dispatch(getMessageChannelQrCode(agencyId, connectionId)),
    [agencyId, dispatch]
  )

  const getMessageChannelStatusAction = useCallback(
    async connectionId => dispatch(getMessageChannelStatus(agencyId, connectionId)),
    [agencyId, dispatch]
  )

  const {
    isQrCodeModalOpen,
    qrCodeValue,
    qrCodeConnectionName,
    isQrLoading,
    qrCodeErrorMessage,
    qrCloseSeconds,
    isQrCloseDisabled,
    qrCodeButtonLabel,
    openQrCodeModal,
    handleCloseQrCodeModal,
    resetQrCodeButtonLabel,
  } = useQrCodeFlow({
    getTargetConnectionId,
    getConnectionName,
    getMessageChannelQrCodeAction,
    getMessageChannelStatusAction,
    fetchConnections,
    onConnectionReady: handleConnectionReady,
    showToast,
  })

  const handleSelectConnection = useCallback(connection => {
    setSelectedConnectionId(connection.id)
  }, [])

  const openConnectionModal = useCallback(
    connection => {
      const statusId = connection?.statusId
      const isAwaitingQr = statusId === MESSAGE_CHANNEL_STATUS.WAITING_QR
      setSelectedConnectionData(connection || null)
      reset({
        name: connection?.name || '',
        phone: connection?.phone || '',
      })
      setIsTestError(false)
      setNewConnectionId(connection?.id || null)
      setIsNewConnectionSaved(true)
      setIsNewConnectionConnected(isAwaitingQr)
      setIsConnectionReady(statusId === MESSAGE_CHANNEL_STATUS.CONNECTED)
      setIsNewConnectionModalOpen(true)
    },
    [reset]
  )

  const handleRowClick = useCallback(
    row => {
      const statusId = row?.statusId
      const isAwaitingConnection = statusId === MESSAGE_CHANNEL_STATUS.WAITING_CONNECTION
      const isAwaitingQr = statusId === MESSAGE_CHANNEL_STATUS.WAITING_QR
      const isConnected = statusId === MESSAGE_CHANNEL_STATUS.CONNECTED
      const isDisconnectedByUser = statusId === MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER
      handleSelectConnection(row)
      setSelectedConnectionData(row || null)
      if (isAwaitingConnection || isAwaitingQr || isConnected || isDisconnectedByUser) {
        openConnectionModal(row)
      }
    },
    [handleSelectConnection, openConnectionModal]
  )

  useEffect(() => {
    fetchConnections()
  }, [fetchConnections])

  useEffect(() => {
    if (!selectedConnectionId && connectionRows.length) {
      handleSelectConnection(connectionRows[0])
    }
  }, [connectionRows, handleSelectConnection, selectedConnectionId])

  const handleNewConnection = useCallback(() => {
    reset({
      name: '',
      phone: '',
    })
    setIsTestError(false)
    setNewConnectionId(null)
    setIsNewConnectionSaved(false)
    setIsNewConnectionConnected(false)
    setIsConnectionReady(false)
    setSelectedConnectionData(null)
    setIsNewConnectionModalOpen(true)
  }, [reset])

  const handleCloseNewConnectionModal = useCallback(() => {
    setIsNewConnectionModalOpen(false)
    setIsNewConnectionSaved(false)
    setIsNewConnectionConnected(false)
    setIsConnectionReady(false)
    setNewConnectionId(null)
    setSelectedConnectionData(null)
    setIsTestError(false)
    reset({
      name: '',
      phone: '',
    })
    resetQrCodeButtonLabel()
  }, [reset, resetQrCodeButtonLabel])

  const handleSaveNewConnection = useCallback(
    async data => {
      try {
        const response = await dispatch(
          createMessageChannel(agencyId, {
            name: data.name,
            testPhoneNumber: parseNumber(data.phone),
          })
        )
        setNewConnectionId(response?.id || null)
        setSelectedConnectionId(response?.id || null)
        setSelectedConnectionData({
          id: response?.id || null,
          name: data.name,
          phone: data.phone,
        })
        setIsNewConnectionSaved(true)
        setIsNewConnectionConnected(false)
        setIsConnectionReady(false)
        await fetchConnections()
      } catch (error) {
        showToast({ type: 'error' })
      }
    },
    [agencyId, dispatch, fetchConnections, showToast]
  )

  const handleConnectNewConnection = useCallback(async () => {
    if (isNewConnectionConnected) {
      openQrCodeModal()
      return
    }
    if (!newConnectionId) {
      showToast({ type: 'error' })
      return
    }
    try {
      setIsConnectingLoading(true)
      const response = await dispatch(connectMessageChannel(agencyId, newConnectionId))
      if (!response?.created) {
        showToast({ type: 'error' })
        return
      }
      showToast({ type: 'success', message: 'Instância criada com sucesso' })
      setIsNewConnectionConnected(true)
      await fetchConnections()
    } catch (error) {
      showToast({ type: 'error' })
    } finally {
      setIsConnectingLoading(false)
    }
  }, [
    agencyId,
    dispatch,
    fetchConnections,
    isNewConnectionConnected,
    newConnectionId,
    openQrCodeModal,
    showToast,
  ])

  const handleTestConnection = useCallback(async () => {
    const targetConnectionId = getTargetConnectionId()
    if (!targetConnectionId) {
      showToast({ type: 'error' })
      return
    }
    const isPhoneValid = await trigger('phone')
    if (!isPhoneValid) {
      setIsTestError(true)
      return
    }
    setIsTestLoading(true)
    try {
      const phone = parseNumber(getValues('phone'))
      const response = await dispatch(
        testMessageChannelConnection(agencyId, targetConnectionId, {
          number: phone,
        })
      )
      if (!response?.sent) {
        showToast({ type: 'error' })
        return
      }
      setIsTestError(false)
      showToast({
        type: 'success',
        message: 'Teste enviado com sucesso! verifique no seu whatsapp.',
      })
      await fetchConnections()
      handleCloseNewConnectionModal()
    } catch (error) {
      setIsTestError(true)
      showToast({
        type: 'error',
        message: 'Mensagem não enviada, verifique o telefone informado para teste',
      })
    } finally {
      setIsTestLoading(false)
    }
  }, [
    agencyId,
    dispatch,
    getTargetConnectionId,
    handleCloseNewConnectionModal,
    getValues,
    trigger,
    showToast,
  ])

  const handleDisconnectConnection = useCallback(async () => {
    const targetConnectionId = getTargetConnectionId()
    if (!targetConnectionId) {
      showToast({ type: 'error' })
      return
    }
    setIsDisconnecting(true)
    try {
      const response = await dispatch(disconnectMessageChannel(agencyId, targetConnectionId))
      if (!response?.disconnected) {
        showToast({ type: 'error' })
        return
      }
      setIsConnectionReady(false)
      setIsNewConnectionConnected(false)
      setSelectedConnectionData(current =>
        current ? { ...current, statusId: MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER } : current
      )
      await fetchConnections()
      showToast({ type: 'success', message: 'Conexão desconectada com sucesso' })
      handleCloseNewConnectionModal()
    } catch (error) {
      showToast({ type: 'error' })
    } finally {
      setIsDisconnecting(false)
    }
  }, [
    agencyId,
    dispatch,
    fetchConnections,
    getTargetConnectionId,
    handleCloseNewConnectionModal,
    showToast,
  ])

  const canShowConnect = isNewConnectionSaved && !isNewConnectionConnected && !isConnectionReady
  const canShowQrCode = isNewConnectionSaved && isNewConnectionConnected && !isConnectionReady
  const canShowTest = isNewConnectionSaved && isConnectionReady
  const canShowDisconnect =
    isNewConnectionSaved && selectedConnectionData?.statusId === MESSAGE_CHANNEL_STATUS.CONNECTED
  const connectButtonLabel =
    selectedConnectionData?.statusId === MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER
      ? 'Reconectar'
      : 'Conectar'

  if (isAgencyLoading || !hasPermissionToViewPage) {
    return <Loader hasBackdrop open />
  }

  return (
    <Grid className={styles.page}>
      <HeaderTitle title="Configuração de conexões" backButtonAction={handleBackNavigation}>
        <Grid className={styles.statusButtons}>
          <Button variant="contained" color="primary" onClick={handleNewConnection}>
            Novo
          </Button>
        </Grid>
      </HeaderTitle>
      <Grid className={styles.contentGrid}>
        <Grid className={styles.tableContainer}>
          <Grid className={styles.mainTable}>
            <Datagrid
              rows={connectionRows}
              columns={connectionColumns}
              getRowId={row => row.id}
              sx={datagridStyle}
              onRowClick={event => handleRowClick(event.row)}
              disableColumnMenu
              disableSelectionOnClick
              checkboxSelection={false}
            />
          </Grid>
        </Grid>
      </Grid>
      <Modal.Root
        open={isNewConnectionModalOpen}
        onClose={handleCloseNewConnectionModal}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <Modal.TitleModal title="Nova conexão" />
        <Divider className={styles.modalDivider} />
        <Modal.Content className={styles.modalContent}>
          <FormProvider {...methods}>
            <form id="new-connection-form" className={styles.modalForm}>
              <BasicInput
                name="name"
                label="Nome da conexão"
                required
                disabled={isNewConnectionSaved}
                defaultValue={selectedConnectionData?.name || ''}
              />
              <MaskBasicInput
                name="phone"
                label="Telefone para teste"
                mask="+55 (99) 9 9999-9999"
                maskChar=""
                required
                defaultValue={selectedConnectionData?.phone || ''}
                rules={{
                  required: true,
                  validate: value =>
                    value?.replace(/\D/g, '').length === 13 || 'Telefone incompleto',
                }}
                disabled={isNewConnectionSaved && !isTestError}
              />
            </form>
          </FormProvider>
        </Modal.Content>
        <Modal.Actions className={styles.modalActions}>
          <Modal.ButtonRed onClick={handleCloseNewConnectionModal}>Cancelar</Modal.ButtonRed>
          {canShowDisconnect && (
            <Modal.ButtonRed onClick={handleDisconnectConnection} disabled={isDisconnecting}>
              Desconectar
            </Modal.ButtonRed>
          )}
          {!isNewConnectionSaved && (
            <Modal.ButtonFullBlue
              onClick={handleSubmit(handleSaveNewConnection)}
              disabled={!isValid}
            >
              Salvar
            </Modal.ButtonFullBlue>
          )}
          {canShowConnect && (
            <Modal.ButtonFullBlue
              onClick={handleConnectNewConnection}
              disabled={isConnectingLoading}
            >
              {connectButtonLabel}
            </Modal.ButtonFullBlue>
          )}
          {canShowQrCode && (
            <Modal.ButtonFullBlue onClick={handleConnectNewConnection} disabled={isQrLoading}>
              {qrCodeButtonLabel}
            </Modal.ButtonFullBlue>
          )}
          {canShowTest && (
            <Modal.ButtonFullBlue onClick={handleTestConnection}>
              Testar conexão
            </Modal.ButtonFullBlue>
          )}
        </Modal.Actions>
      </Modal.Root>
      <Modal.Root
        open={isQrCodeModalOpen}
        onClose={handleCloseQrCodeModal}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <Modal.TitleModal
          title={qrCodeConnectionName ? `QR Code - ${qrCodeConnectionName}` : 'QR Code'}
        />
        <Divider className={styles.modalDivider} />
        <Modal.Content className={styles.qrCodeContent}>
          <span className={styles.qrCodeHint}>
            A tela será fechada automaticamente ao escanear o QR Code e conectar com sucesso.
          </span>
          {qrCodeValue && (
            <span className={styles.qrCodeImageWrapper}>
              <img src={qrCodeValue} alt="QR Code" className={styles.qrCodeImage} />
            </span>
          )}
          {qrCodeErrorMessage && <span className={styles.qrCodeError}>{qrCodeErrorMessage}</span>}
        </Modal.Content>
        <Modal.Actions className={styles.modalActions}>
          <Modal.ButtonRed onClick={handleCloseQrCodeModal} disabled={isQrCloseDisabled}>
            {isQrCloseDisabled ? `Fechar (${qrCloseSeconds}s)` : 'Fechar'}
          </Modal.ButtonRed>
        </Modal.Actions>
      </Modal.Root>
      <Loader
        hasBackdrop
        open={isConnectingLoading || isQrLoading || isDisconnecting || isTestLoading}
        label="Aguarde..."
      />
    </Grid>
  )
}

export default ConnectionConfig
